'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

const liveStats = [
    { value: '12k+', label: 'Active learners', color: 'text-primary' },
    { value: '500+', label: 'Live instructors', color: 'text-orange' },
    { value: '95%', label: 'Satisfaction rate', color: 'text-green' }];


export default function LearnHeroSection() {
    const blob1Ref = useRef<HTMLDivElement>(null);
    const blob2Ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let raf: number;
        let mouseX = 0;
        let mouseY = 0;
        let currentX = 0;
        let currentY = 0;

        const handleMouseMove = (e: MouseEvent) => {
            mouseX = (e.clientX / window.innerWidth - 0.5) * 50;
            mouseY = (e.clientY / window.innerHeight - 0.5) * 30;
        };

        const animate = () => {
            currentX += (mouseX - currentX) * 0.04;
            currentY += (mouseY - currentY) * 0.04;
            if (blob1Ref.current) {
                blob1Ref.current.style.transform = `translate(${currentX * 0.6}px, ${currentY * 0.6}px)`;
            }
            if (blob2Ref.current) {
                blob2Ref.current.style.transform = `translate(${-currentX * 0.4}px, ${-currentY * 0.4}px)`;
            }
            raf = requestAnimationFrame(animate);
        };

        window.addEventListener('mousemove', handleMouseMove, { passive: true });
        raf = requestAnimationFrame(animate);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            cancelAnimationFrame(raf);
        };
    }, []);

    return (
        <section className="relative min-h-[90vh] flex flex-col justify-center overflow-hidden bg-background pt-16">
            {/* Atmospheric background */}
            <div className="absolute inset-0 grid-bg pointer-events-none" />
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div
                    ref={blob1Ref}
                    className="absolute top-[-10%] right-[-5%] w-[700px] h-[700px] blob-primary animate-blob will-change-transform opacity-60" />
                <div
                    ref={blob2Ref}
                    className="absolute bottom-[-15%] left-[-10%] w-[600px] h-[600px] blob-orange animate-blob-delay will-change-transform opacity-40" />
                <div className="noise-overlay absolute inset-0" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 w-full py-16 md:py-20">
                <div className="max-w-4xl">
                    {/* Eyebrow */}
                    <div className="animate-hero-1 mb-6">
                        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-blue text-primary text-sm font-semibold">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                            </span>
                            Wisly Learn — For students & learners
                        </span>
                    </div>

                    {/* Headline */}
                    <div className="animate-hero-2 mb-6">
                        <h1 className="text-hero font-extrabold text-foreground">
                            Learn from real people,{' '}
                            <span className="text-gradient-blue">in real time.</span>
                        </h1>
                    </div>

                    {/* Supporting copy */}
                    <div className="animate-hero-3 mb-8">
                        <p className="text-xl md:text-2xl text-muted-foreground font-body leading-relaxed max-w-2xl">
                            No recordings. No passive watching.{' '}
                            <span className="text-foreground font-semibold">
                                Every class on Wisly is live — you book a time, show up, and learn directly from your instructor.
                            </span>
                        </p>
                    </div>

                    {/* CTAs */}
                    <div className="animate-hero-4 flex flex-col sm:flex-row gap-3 mb-10">
                        <Link
                            href="#how-it-works"
                            className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-primary text-primary-foreground rounded-full text-base font-semibold hover:bg-primary/90 shadow-blue hover:shadow-blue-lg hover:-translate-y-0.5 transition-all duration-300">

                            See how it works
                            <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Link>
                        <Link
                            href="#categories"
                            className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-card text-foreground border border-border rounded-full text-base font-semibold hover:border-primary/30 hover:bg-primary/5 hover:-translate-y-0.5 transition-all duration-300 shadow-card">

                            Browse live sessions
                        </Link>
                    </div>

                    {/* Stats row */}
                    <div className="animate-hero-5 flex flex-wrap gap-6">
                        {liveStats.map((stat) =>
                            <div key={stat.label} className="flex items-center gap-2">
                                <span className={`text-2xl font-extrabold ${stat.color}`}>{stat.value}</span>
                                <span className="text-muted-foreground text-sm font-body">{stat.label}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Floating live session cards row */}
                <div className="animate-hero-5 mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
                    {[
                        {
                            img: "https://img.rocket.new/generatedImages/rocket_gen_img_1c42a389c-1774790296393.png",
                            alt: 'Chef teaching live cooking class in a warm professional kitchen',
                            title: 'Nigerian Cooking',
                            instructor: 'Chef Amaka',
                            schedule: 'Tue & Thu · 6pm',
                            type: 'Live Slot',
                            typeColor: 'text-red-500 bg-red-500/10 border-red-500/20',
                            live: true
                        },
                        {
                            img: "https://img.rocket.new/generatedImages/rocket_gen_img_19707f093-1772097118456.png",
                            alt: 'Developer teaching live coding bootcamp session on laptop',
                            title: 'Full-Stack Dev',
                            instructor: 'Tunde Okafor',
                            schedule: '8 weeks · Mon–Fri',
                            type: 'Bootcamp',
                            typeColor: 'text-primary bg-primary/10 border-primary/20',
                            live: false
                        },
                        {
                            img: "https://images.unsplash.com/photo-1657004508178-5374531174db",
                            alt: 'Teacher explaining mathematics on whiteboard in a bright classroom',
                            title: 'SS3 Mathematics',
                            instructor: 'Bright Academy',
                            schedule: 'Mon–Fri · 9am',
                            type: 'Program',
                            typeColor: 'text-green bg-green/10 border-green/20',
                            live: false
                        }].
                        map((card) =>
                            <div key={card.title} className="bg-card rounded-2xl border border-border overflow-hidden shadow-card group card-hover cursor-pointer">
                                <div className="h-36 overflow-hidden relative">
                                    <AppImage
                                        src={card.img}
                                        alt={card.alt}
                                        width={300}
                                        height={144}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    {card.live &&
                                        <div className="absolute top-2 right-2">
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500 text-white text-xs font-bold">
                                                <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />
                                                LIVE
                                            </span>
                                        </div>
                                    }
                                </div>
                                <div className="p-4">
                                    <p className="font-bold text-foreground text-sm mb-0.5">{card.title}</p>
                                    <p className="text-muted-foreground text-xs font-body mb-2">{card.instructor}</p>
                                    <div className="flex items-center justify-between">
                                        <span className="text-primary text-xs font-semibold">🗓 {card.schedule}</span>
                                        <span className={`pill-tag border text-xs ${card.typeColor}`}>{card.type}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                </div>
            </div>
        </section>);

}